import {PolymerElement, html} from '@polymer/polymer';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {InputBehavior} from '../behaviors/input-behavior.js';
import {InputSharedStylesHTML} from '../shared-styles/input.js';
import {ResizableBehavior} from '../behaviors/resizable-behavior.js';

class KeliTextarea extends ScopeBinding(InputBehavior(ResizableBehavior(PolymerElement))) {
  static get template() {
    return html`
${InputSharedStylesHTML}
<style>
  :host {
    display: block;
    height: auto !important;
  }

  .hidden {
    display: none;
    visibility: hidden;
  }

  #label {
    position: static !important;
    font-size: 1em !important;
    cursor: default;
    display: block;
    font-weight: 300 !important;
    color: var(--keli-field-label-color);
  }

  #help {
    position: absolute;
    right: 0;
    font-size: .7em;
    top: 0;
    color: var(--keli-text-subtle);
    cursor: help;
  }

  #help:hover {
    color: var(--keli-text);
  }

  #input {
    outline: none;
    resize: none;
    box-sizing: border-box;
    width: 100%;
    min-height: var(--keli-input-field-min-height, 7em);
    max-height: 28em;
    font-weight: 400;
    font-size: .9em;
    font-family: inherit;
    padding: var(--keli-input-field-padding, .5em);
    border: var(--keli-input-field-border, 2px solid var(--keli-field-border-color));
    height: auto;
  }

  :host::after {
    content: none;
  }

  :host(.focused) #input {
    margin-bottom: 0 !important;
    border-color: var(--keli-field-border-color-active);
  }

  :host(.focused) #label {
    color: var(--keli-field-label-color-active);
  }

  :host(.styleless) #input {
    border: none !important;
    min-height: 0;
    max-height: none !important;
  }

  /* If the container is set for flexing, go ahead and flex. */
  #container {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    box-sizing: border-box;
    max-height: 100%;
  }

  #input {
    flex: 1;
  }

  #resizeBottom {
    position: absolute;
    min-height: 8px;
    opacity: 0;
    cursor: ns-resize;
    bottom: -3px;
    right: 0;
    left: 0;
  }

  #shortcut {
    right: 2px;
    top: auto;
  }
</style>

<div id="container">
  <span id="label" class$="[[_computeLabelClass(caption, _initialized)]]">[[caption]]</span>
  <sl-icon id="help" name="help-circle-outline" class$="[[_computeHelpClass(helpText, _initialized)]]"></sl-icon>
  <textarea id="input" placeholder="[[placeholder]]" value="{{currentValue::input}}"></textarea>
  <div id="resizeBottom"></div>
</div>
<span id="shortcut" class$="[[_computeShortcutClass(formattedShortcutKey, _initialized)]]">[[formattedShortcutKey]]</span>`;
  }

  static get is() {
    return 'keli-textarea';
  }

  static get properties() {
    return {
      _initialized: {type: Boolean},
      height: {type: Number, scopevar: {options: {view: true}}, observer: 'heightChanged'},
      resize: {type: Boolean, scopevar: true, observer: 'resizeChanged'},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['textarea']},
      placeholder: String,
      propagateEnter: Boolean,
      selectOnFocus: {type: Boolean, value: false}
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._initialized = true;

    this.resizableParentElement = this.$.input;
    this.resizableHandleElements = [
      {element: this.$.resizeBottom, direction: 's'}
    ];

    this.$.input.setAttribute('rows', '1');
    afterNextRender(this.$.input, () => {
      this.setTextareaHeight();
    });

    this.autoCommitter = window.setInterval(() => {
      if (this.fireKeliAccepted) {
        this.displayValue = this.currentValue;
      }
    }, 30000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this._handleInputJob && this._handleInputJob.isActive()) {
      this._handleInputJob.flush();
    }

    if (this.autoCommitter) {
      window.clearInterval(this.autoCommitter);
    }
  }

  _computeLabelClass(caption) {
    const classList = [];
    if (!caption) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeHelpClass(helpText) {
    const classList = [];
    if (!helpText) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeShortcutClass(formattedShortcutKey) {
    const classList = [];
    if (!formattedShortcutKey) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  handleKeyDown(event) {
    if (!this.propagateEnter && KeliInterface.Hotkeys.enter.includes(event.code)) {
      event.stopPropagation();
    }

    if ([KeliInterface.Hotkeys.arrows.up, KeliInterface.Hotkeys.arrows.down].includes(event.code)) {
      const caret = this.getTextareaCaretY(this.$.input, this.$.input.selectionStart);
      const isFirstLine = (caret.y === 0);
      const isLastLine = (caret.maxY <= caret.y);
      if (event.code === KeliInterface.Hotkeys.arrows.up && !isFirstLine) {
        event.stopPropagation();
      }
      if (event.code === KeliInterface.Hotkeys.arrows.down && !isLastLine) {
        event.stopPropagation();
      }
    }
  }

  handleInput() {
    // Debounce when the user changes the value so we can auto-save when they stop typing
    this._handleInputJob = Debouncer.debounce(this._handleInputJob, timeOut.after(5000), () => {
      if (this.fireKeliAccepted) {
        this.displayValue = this.currentValue;
      }
    });
    this.setTextareaHeight();
  }

  currentValueChanged() {
    this.setTextareaHeight();
  }

  heightChanged(newValue) {
    this.$.input.style.height = '';
    if (newValue) {
      this.$.input.style.height = this.$.input.style.minHeight = `${parseInt(newValue, 10)}px`;
    }
    this.setTextareaHeight();
  }

  resizeChanged() {
    this.setTextareaHeight();
  }

  handleResizableResizeElement(event) {
    event.stopPropagation();
    if (event.detail.height) {
      this.height = event.detail.height;
    }
  }

  handleResizableResizeElementEnded(event) {
    event.stopPropagation();
  }

  setTextareaHeight() {
    if (this.resize) {
      this.$.input.style.height = this.$.input.style.flexBasis = 'auto';
      this.$.input.style.height = this.$.input.style.flexBasis = `${this.$.input.scrollHeight}px`;
    } else {
      this.$.input.style.flexBasis = 'auto';
      if (this.height) {
        this.$.input.style.height = this.$.input.style.minHeight = `${parseInt(this.height, 10)}px`;
      }
    }
  }

  getTextareaCaretY(input, selectionPoint) {
    const div = document.createElement('div');
    const copyStyle = getComputedStyle(input);
    for (const prop of copyStyle) {
      div.style[prop] = copyStyle[prop];
    }
    div.style.visibility = 'hidden';
    div.style.height = div.style.minHeight = div.style.flexBasis = 'auto';
    div.style.padding = '0';
    div.style.border = 'none';
    const inputValue = input.value;
    div.textContent = inputValue.substr(0, selectionPoint);
    const span = document.createElement('span');
    span.textContent = inputValue.substr(selectionPoint) || '.';
    div.appendChild(span);
    document.body.appendChild(div);
    const { offsetTop: spanY } = span;
    div.textContent = input.value;
    span.textContent = '.';
    div.appendChild(span);
    const { offsetTop: maxSpanY } = span;
    document.body.removeChild(div);
    return {
      y: spanY,
      maxY: maxSpanY
    };
  }
}

customElements.define(KeliTextarea.is, KeliTextarea);
export {KeliTextarea};
